import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq } from 'ember-truth-helpers';

type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
export type Status = 'started' | 'complete' | 'failed';

export const EXPORT_STATUS = {
  STARTED: 'started' as const,
  COMPLETE: 'complete' as const,
  FAILED: 'failed' as const,
};

interface ExportStatusSignature {
  Element: HTMLDivElement;
  Args: {
    position?: Position;
    status: Status;
    title: string;
    message: string;
    linkText?: string;
    linkHref?: string;
    onClick?: () => void;
  };
}

export default class ExportStatus extends Component<ExportStatusSignature> {
  @tracked isMinimized = false;

  get position(): Position {
    return this.args.position || 'top-right';
  }

  get statusIcon(): string {
    switch (this.args.status) {
      case EXPORT_STATUS.STARTED:
        return 'loading-spinner';
      case EXPORT_STATUS.COMPLETE:
        return 'circle-check';
      case EXPORT_STATUS.FAILED:
        return 'circle-cross-red';
      default:
        return '';
    }
  }

  get statusColorClass(): string {
    switch (this.args.status) {
      case EXPORT_STATUS.STARTED:
        return 'status-started';
      case EXPORT_STATUS.COMPLETE:
        return 'status-complete';
      case EXPORT_STATUS.FAILED:
        return 'status-failed';
      default:
        return '';
    }
  }

  get style(): SafeString {
    const styles = this.calculatePosition();
    return htmlSafe(styles);
  }

  calculatePosition(): string {
    const baseStyles = 'position: fixed; z-index: 100000000;';
    const offset = '24px';

    switch (this.position) {
      case 'top-right':
        return `${baseStyles} top: ${offset}; right: ${offset};`;
      case 'top-left':
        return `${baseStyles} top: ${offset}; left: ${offset};`;
      case 'bottom-right':
        // Use different positions based on minimized state
        if (this.isMinimized) {
          return `${baseStyles} bottom: 32px; right: 128px;`;
        }
        return `${baseStyles} bottom: 45px; right: 140px;`;
      case 'bottom-left':
        return `${baseStyles} bottom: ${offset}; left: ${offset};`;
      default:
        return `${baseStyles} top: ${offset}; right: ${offset};`;
    }
  }

  @action
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  @action
  close(): void {
    this.args.onClick?.();
  }

  <template>
    <div
      class="export-status {{this.statusColorClass}} {{if this.isMinimized 'minimized'}}"
      style={{this.style}}
      role="alert"
      aria-live="polite"
      ...attributes
    >
      <div class="export-status-header">
        <div class="flex items-center gap-2">
          <div class="status-icon {{this.statusColorClass}}">
            {{#if (eq @status "started")}}
              <div class="spinner"></div>
            {{else if (eq @status "failed")}}
              {{svgJar this.statusIcon class="h-6 w-6"}}
            {{else}}
              {{svgJar this.statusIcon class="h-6 w-6"}}
            {{/if}}
          </div>
          <h3 class="export-status-title">{{@title}}</h3>
        </div>
        <div class="export-status-actions">
          <button
            type="button"
            class="action-button minimize-button"
            {{on "click" this.toggleMinimize}}
            aria-label={{if this.isMinimized "Expand" "Minimize"}}
          >
            {{svgJar (if this.isMinimized "chevron-down" "chevron-up") class="h-3 w-3"}}
          </button>
          <button type="button" class="action-button close-button" {{on "click" this.close}} aria-label="Close">
            {{svgJar "x" class="h-4 w-4"}}
          </button>
        </div>
      </div>

      {{#unless this.isMinimized}}
        <div class="export-status-body">
          <p class="export-status-message">
            {{@message}}
            {{#if @linkText}}
              <a href={{@linkHref}} class="export-status-link">{{@linkText}}</a>
            {{/if}}
          </p>
        </div>
      {{/unless}}
    </div>
  </template>
}
